import { Link } from 'wouter';
import {
  Leaf, Droplets, Wind, Layers,
  Sun, Flower2, Sparkles, Gift,
  PartyPopper, Heart, Star, Ribbon,
  Trees, Shrub, Mountain, Gem,
  Box, Crown, CircleDot, Package,
} from 'lucide-react';
import { collectionUrl } from '@/lib/collectionUrl';

/* ── Tokens ─────────────────────────────────────────────── */
const LEFT_BG = '#ffffff';
const WHITE   = '#ffffff';
const DARK    = '#2A1F14';
const GOLD    = '#e05590';
const ROSE    = '#9B2335';
const MUTED   = 'rgba(42,31,20,0.58)';
const ICON_BG = '#FAE8E8';

type Icon = React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
interface BannerItem { Icon: Icon; title: string; desc: string }
interface BannerContent {
  leftEyebrow: string;
  heading: string;
  desc: string;
  rightEyebrow: string;
  items: BannerItem[];
  pillsLabel: string;
  pills: { label: string; handle: string }[];
}

export const SPECJALNY_BANNER_HANDLES = new Set([
  'lasy-w-szkle',
  'balony-z-helem',
  'kwiaty-doniczkowe',
  'wieczne-roze',
]);

const CONTENT: Record<string, BannerContent> = {

  /* ─── Lasy w szkle ──────────────────────────────────────── */
  'lasy-w-szkle': {
    leftEyebrow: 'DLACZEGO LAS W SZKLE TO WYJĄTKOWY PREZENT?',
    heading: 'Kawałek natury zamknięty w szkle',
    desc: 'Lasy w szkle to miniaturowe ekosystemy, które zachwycają urodą i praktycznością. Nie wymagają codziennej opieki, a przez lata cieszą oko delikatną zielenią i mchem. To prezent, który zostaje na długo i przypomina o darczyńcy każdego dnia.',
    rightEyebrow: 'DLACZEGO WARTO WYBRAĆ LAS W SZKLE?',
    items: [
      { Icon: Trees,    title: 'Trwałość',              desc: 'Nie więdnie - cieszy pięknem przez wiele lat bez wymiany.' },
      { Icon: Droplets, title: 'Minimalna pielęgnacja', desc: 'Podlewanie raz na kilka tygodni - idealny dla zapracowanych.' },
      { Icon: Sparkles, title: 'Oryginalność',          desc: 'Każdy las w szkle to niepowtarzalna, ręcznie tworzona kompozycja.' },
      { Icon: Gift,     title: 'Dekoracja wnętrz',      desc: 'Elegancki akcent, który pasuje do każdego stylu wnętrza.' },
    ],
    pillsLabel: 'ODKRYJ INNE WYJĄTKOWE PREZENTY',
    pills: [
      { label: 'Kwiaty doniczkowe', handle: 'kwiaty-doniczkowe' },
      { label: 'Wieczne róże',      handle: 'wieczne-roze' },
      { label: 'Kosze kwiatowe',    handle: 'kosze-kwiatowe' },
      { label: 'Flower boxy',       handle: 'kwiaty-w-torebce' },
      { label: 'Bukiety',           handle: 'bukiety' },
      { label: 'Cała oferta',       handle: 'wszystkie' },
    ],
  },

  /* ─── Balony z helem ────────────────────────────────────── */
  'balony-z-helem': {
    leftEyebrow: 'DLACZEGO BALONY Z HELEM ROBIĄ WRAŻENIE?',
    heading: 'Radość, która unosi się w powietrzu',
    desc: 'Balony z helem to doskonałe uzupełnienie każdej uroczystości. Efektowne, kolorowe i pełne radości - sprawdzą się na urodziny, baby shower, wesele i każdą okazję wymagającą wyjątkowej, świątecznej oprawy. Dostarczamy je już napełnione.',
    rightEyebrow: 'POPULARNE ZASTOSOWANIA BALONÓW Z HELEM',
    items: [
      { Icon: PartyPopper, title: 'Urodziny',    desc: 'Kolorowa dekoracja na przyjęcie - napis, cyfra lub foliowy balon.' },
      { Icon: Heart,       title: 'Walentynki',  desc: 'Romantyczne balony w kształcie serc - wyraź uczucia z rozmachem.' },
      { Icon: Star,        title: 'Baby shower', desc: 'Pastelowe zestawy balonowe idealne na powitanie nowego życia.' },
      { Icon: Ribbon,      title: 'Inne okazje', desc: 'Imieniny, rocznica, wesele - balony pasują na każde świętowanie.' },
    ],
    pillsLabel: 'POPULARNE ZESTAWY I OKAZJE',
    pills: [
      { label: 'Urodziny',     handle: 'urodziny' },
      { label: 'Walentynki',   handle: 'walentynki' },
      { label: 'Rocznica',     handle: 'rocznica' },
      { label: 'Baby shower',  handle: 'niespodzianka' },
      { label: 'Bukiety',      handle: 'bukiety' },
      { label: 'Cała oferta',  handle: 'wszystkie' },
    ],
  },

  /* ─── Kwiaty doniczkowe ─────────────────────────────────── */
  'kwiaty-doniczkowe': {
    leftEyebrow: 'DLACZEGO KWIATY DONICZKOWE TO WYJĄTKOWY PREZENT?',
    heading: 'Prezent, który rozkwita przez cały rok',
    desc: 'Kwiaty doniczkowe to ponadczasowy prezent, który cieszy dłużej niż klasyczny bukiet. Żywa roślina w domu poprawia nastrój, oczyszcza powietrze i przypomina o darczyńcy każdego dnia. Pomagamy dobrać gatunek idealny dla obdarowanej osoby.',
    rightEyebrow: 'NAJPOPULARNIEJSZE KWIATY DONICZKOWE',
    items: [
      { Icon: Flower2, title: 'Orchidee',          desc: 'Eleganckie i długo kwitnące - królowe kwiatów doniczkowych.' },
      { Icon: Sun,     title: 'Sukulenty i kaktusy', desc: 'Minimalistyczne i niewymagające - idealne dla zapracowanych.' },
      { Icon: Leaf,    title: 'Skrzydłokwiaty',    desc: 'Oczyszczają powietrze i kwitną nawet przy słabym oświetleniu.' },
      { Icon: Shrub,   title: 'Paprocie i zieleń', desc: 'Bujne rośliny dekoracyjne nadające wnętrzu leśny charakter.' },
    ],
    pillsLabel: 'ODKRYJ INNE ROŚLINNE PREZENTY',
    pills: [
      { label: 'Lasy w szkle',  handle: 'lasy-w-szkle' },
      { label: 'Wieczne róże',  handle: 'wieczne-roze' },
      { label: 'Bukiety',       handle: 'bukiety' },
      { label: 'Kosze kwiatowe', handle: 'kosze-kwiatowe' },
      { label: 'Na urodziny',   handle: 'urodziny' },
      { label: 'Cała oferta',   handle: 'wszystkie' },
    ],
  },

  /* ─── Wieczne róże ──────────────────────────────────────── */
  'wieczne-roze': {
    leftEyebrow: 'CZYM SĄ WIECZNE RÓŻE I DLACZEGO ZACHWYCAJĄ?',
    heading: 'Piękno róż, które nie przemija',
    desc: 'Wieczne róże to naturalne kwiaty poddane specjalnej konserwacji, która zachowuje ich świeżość i piękno na lata. Nie wymagają wody ani codziennej pielęgnacji, a wyglądają jak świeżo ścięte. To luksusowy prezent na każdą wyjątkową okazję.',
    rightEyebrow: 'POPULARNE FORMY WIECZNYCH RÓŻ',
    items: [
      { Icon: Box,      title: 'Flower boxy',          desc: 'Eleganckie pudełka z wiecznymi różami - gotowe do wręczenia.' },
      { Icon: Gem,      title: 'Kopuły z różą',        desc: 'Szklana kopuła jak z bajki - romantyczny symbol wiecznej miłości.' },
      { Icon: Crown,    title: 'Kompozycje ekskluzywne', desc: 'Luksusowe zestawy z wiecznymi różami i dekoracjami.' },
      { Icon: Package,  title: 'Personalizowane',      desc: 'Kolor, rozmiar, dedykacja - stwórzmy coś wyjątkowego razem.' },
    ],
    pillsLabel: 'ODKRYJ INNE PONADCZASOWE PREZENTY',
    pills: [
      { label: 'Róże',            handle: 'roze' },
      { label: 'Lasy w szkle',    handle: 'lasy-w-szkle' },
      { label: 'Kwiaty doniczkowe', handle: 'kwiaty-doniczkowe' },
      { label: 'Flower boxy',     handle: 'kwiaty-w-torebce' },
      { label: 'Na rocznicę',     handle: 'rocznica' },
      { label: 'Cała oferta',     handle: 'wszystkie' },
    ],
  },
};

/* ─── Component ─────────────────────────────────────────── */
export function SpecjalnyBanner({ handle }: { handle: string }) {
  const content = CONTENT[handle];
  if (!content) return null;

  const { leftEyebrow, heading, desc, rightEyebrow, items, pillsLabel, pills } = content;

  return (
    <section style={{ fontFamily: 'DM Sans, sans-serif' }}>

      {/* ── Two-panel row ── */}
      <div className="page-container" style={{ display: 'flex', flexWrap: 'wrap', columnGap: 'clamp(32px, 5vw, 72px)' }}>

        {/* Left — cream editorial */}
        <div style={{
          flex: '1 1 340px',
          background: LEFT_BG,
          paddingBlock: 'clamp(48px,6vw,80px)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <p style={{
            fontSize: 10, letterSpacing: '0.18em', fontWeight: 700,
            color: GOLD, textTransform: 'uppercase', margin: '0 0 20px',
          }}>
            {leftEyebrow}
          </p>
          <h2 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(2rem,3.5vw,3rem)', fontWeight: 400,
            color: DARK, margin: '0 0 20px', lineHeight: 1.12,
          }}>
            {heading}
          </h2>
          <p style={{
            fontSize: 14, lineHeight: 1.75, color: MUTED,
            margin: '0 0 28px', maxWidth: 400,
          }}>
            {desc}
          </p>
          <p style={{
            fontSize: 12, color: ROSE, fontWeight: 600,
            letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 7,
          }}>
            <Leaf size={14} color={ROSE} strokeWidth={2} />
            Bukietowa - kwiaty z pasją od 2016 roku
          </p>
        </div>

        {/* Right — white icon grid */}
        <div style={{
          flex: '1 1 340px',
          background: WHITE,
          paddingBlock: 'clamp(48px,6vw,80px)',
        }}>
          <p style={{
            fontSize: 10, letterSpacing: '0.18em', fontWeight: 700,
            color: GOLD, textTransform: 'uppercase', margin: '0 0 36px',
          }}>
            {rightEyebrow}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4" style={{
            gap: 'clamp(16px,3vw,32px)',
          }}>
            {items.map(({ Icon, title, desc: itemDesc }) => (
              <div key={title} style={{ textAlign: 'center' }}>
                <div style={{
                  width: 'min(80px, 100%)', aspectRatio: '1',
                  borderRadius: '50%',
                  background: ICON_BG,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 14px',
                }}>
                  <Icon size={36} color={ROSE} strokeWidth={1.4} />
                </div>
                <p style={{
                  fontFamily: 'Cormorant Garamond, Georgia, serif',
                  fontSize: 15, fontWeight: 600, color: DARK, margin: '0 0 6px',
                }}>
                  {title}
                </p>
                <p style={{ fontSize: 12, lineHeight: 1.55, color: MUTED, margin: 0 }}>
                  {itemDesc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Pills bar ── */}
      <div className="page-container" style={{
        background: '#ffffff',
        borderTop: '1px solid rgba(210,60,120,0.10)',
        paddingBlock: 'clamp(20px,3vw,32px)',
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: 10, letterSpacing: '0.18em', fontWeight: 700,
          color: GOLD, textTransform: 'uppercase', margin: '0 0 14px',
        }}>
          {pillsLabel}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
          {pills.map(({ label, handle: h }) => (
            <Link
              key={h}
              href={collectionUrl(h)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 20px',
                border: '1px solid rgba(155,35,53,0.22)',
                borderRadius: 999,
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 12, fontWeight: 500,
                color: DARK, textDecoration: 'none',
                background: WHITE,
                transition: 'all 0.18s',
              }}
            >
              <Leaf size={12} color={ROSE} strokeWidth={2} />
              {label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
